'use client';

import Script from 'next/script';
import { useEffect, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { appConfig } from '@/config/app';
import { salesforceConfig } from '@/config/salesForce';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { sanitizeNumber } from '@/utils/format';
import { formatAdvisorWhatsAppLink } from '@/utils/messenger';

export const isBusinessHour = (date = new Date()) => {
  const hour = date.getHours();
  return hour >= 9 && hour < 18;
};

export const SFChatScript = () => {
  const { data: session } = useSession();
  const { data: dataCustomer } = useGetCustomerStatus();
  const { data: advisor } = useGetCustomerAdvisor();

  const user = useMemo(
    () =>
      (session && session.user) || { document: null, email: null, name: null },
    [session]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener(
        'click',
        (e) => {
          const isSFButton = e
            .composedPath()
            .some(
              (el) =>
                el instanceof HTMLElement &&
                el.classList.contains('embeddedMessagingConversationButton')
            );
          if (isSFButton && !isBusinessHour() && dataCustomer?.isEqiDigital) {
            if (advisor) {
              const message = `Olá ${advisor?.advisorName.split(' ')[0]}, estou no EQI+ e gostaria de tirar uma dúvida `;

              const url = formatAdvisorWhatsAppLink({
                message: message,
                number: sanitizeNumber(advisor?.advisorMobilePhone ?? ''),
              });

              window.open(url, '_blank');
            } else {
              window.open(
                `${appConfig.support.whatsAppUrlBase}?phone=${appConfig.support.number}&text=${appConfig.support.message}`
              );
            }
            e.stopImmediatePropagation();
          }
        },
        { capture: true }
      );

      window.addEventListener('onEmbeddedMessagingReady', () => {
        const { firstName, lastName } = separateFirstAndLastName(user.name);
        window.embeddedservice_bootstrap?.prechatAPI.setVisiblePrechatFields({
          _firstName: {
            value: firstName,
            isEditableByEndUser: false,
          },
          _lastName: {
            value: lastName,
            isEditableByEndUser: false,
          },
          _email: {
            value: user.email ?? '',
            isEditableByEndUser: false,
          },
        });
        window.embeddedservice_bootstrap?.prechatAPI.setHiddenPrechatFields({
          cpfCnpj: user.document ?? '',
        });
      });
    }
  }, [user, dataCustomer, advisor]);

  if (!appConfig.isProduction) {
    return <></>;
  }

  return (
    <Script
      src={`${salesforceConfig.instanceUrl}/${salesforceConfig.salesforceChat.chatName}/assets/js/bootstrap.min.js`}
      onLoad={initESW}
    />
  );
};

const initESW = () => {
  const embeddedservice_bootstrap = window.embeddedservice_bootstrap;
  if (!embeddedservice_bootstrap) {
    return;
  }

  embeddedservice_bootstrap.settings.language = 'pt_BR';

  embeddedservice_bootstrap.init(
    salesforceConfig.salesforceChat.queueId,
    salesforceConfig.salesforceChat.queueName,
    `${salesforceConfig.instanceUrl}/${salesforceConfig.salesforceChat.chatName}`,
    { scrt2URL: salesforceConfig.salesforceChat.scrt2URL }
  );
};

const separateFirstAndLastName = (name: string | null = '') => {
  if (!name) return { firstName: 'Usuário', lastName: 'sem nome' };
  const [firstName, ...lastName] = name.split(' ');
  const joinedLastName = lastName.length > 0 ? lastName.join(' ') : '';
  return { firstName, lastName: joinedLastName };
};
